import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { supabase } from '../lib/supabase';
import { DeleteModal } from './DeleteModal';
import { useToast } from './Toast';

interface AccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ActivePanel = 'account' | 'password';

const MAX_AVATAR_SIZE = 4 * 1024 * 1024; // 4MB
const ALLOWED_AVATAR_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

// Check if avatar URL is from our Supabase storage (user uploaded)
const isUploadedAvatar = (url: string | null | undefined): boolean => {
  if (!url) return false;
  return url.includes('supabase') && url.includes('/storage/');
};

// Check if avatar URL is a valid displayable avatar (uploaded or OAuth)
const hasValidAvatar = (url: string | null | undefined): boolean => {
  if (!url) return false;
  // Accept Supabase storage uploads
  if (url.includes('supabase') && url.includes('/storage/')) return true;
  // Accept Google OAuth avatars
  if (url.includes('googleusercontent.com')) return true;
  // Accept other common OAuth providers
  if (url.includes('githubusercontent.com')) return true; // GitHub
  if (url.includes('platform-lookaside.fbsbx.com')) return true; // Facebook
  if (url.includes('pbs.twimg.com')) return true; // Twitter
  return false;
};

export function AccountModal({ isOpen, onClose }: AccountModalProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, profile, viewingAs, signOut, fetchProfile } = useAuthStore();
  const [activePanel, setActivePanel] = useState<ActivePanel>('account');
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Account form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [accountLoading, setAccountLoading] = useState(false);
  
  // Avatar state
  const [avatarLoading, setAvatarLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Check if user signed up with OAuth (no password set)
  const isOAuthUser = user?.app_metadata?.provider !== 'email' && !user?.app_metadata?.providers?.includes('email');
  
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle open/close animations
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Double RAF ensures browser has painted display:flex before transitioning opacity
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else if (isVisible) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setActivePanel('account');
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isVisible]);

  // Initialize form with profile data
  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || '');
      setLastName(profile.last_name || '');
    }
    if (user) {
      setEmail(user.email || '');
    }
  }, [profile, user]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !showDeleteModal) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isVisible, onClose, showDeleteModal]);

  const handleSignOut = async () => {
    await signOut();
    onClose();
    navigate('/login');
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    // Validate file type
    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      toast.error('Please upload a PNG, JPG, JPEG, or WebP image.');
      return;
    }

    // Validate file size
    if (file.size > MAX_AVATAR_SIZE) {
      toast.error('Image must be less than 4MB.');
      return;
    }

    setAvatarLoading(true);

    try {
      // Delete old avatar if exists
      if (profile?.avatar_url && isUploadedAvatar(profile.avatar_url)) {
        const oldPath = profile.avatar_url.split('/').pop();
        if (oldPath) {
          await supabase.storage.from('avatars').remove([`${user.id}/${oldPath}`]);
        }
      }

      // Upload new avatar
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Update profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      await fetchProfile(user.id);
      toast.success('Avatar updated successfully');
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Failed to upload avatar. Please try again.');
    } finally {
      setAvatarLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAvatar = async () => {
    if (!user || !profile?.avatar_url) return;

    setAvatarLoading(true);

    try {
      // Delete from Supabase storage if it's an uploaded avatar
      if (isUploadedAvatar(profile.avatar_url)) {
        const pathParts = profile.avatar_url.split('/avatars/');
        if (pathParts[1]) {
          await supabase.storage.from('avatars').remove([pathParts[1]]);
        }
      }
      // For OAuth avatars, we just clear the URL (nothing to delete from storage)

      // Update profile to remove avatar
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('id', user.id);

      if (error) throw error;

      await fetchProfile(user.id);
      toast.success('Avatar removed');
    } catch (error) {
      console.error('Error removing avatar:', error);
      toast.error('Failed to remove avatar. Please try again.');
    } finally {
      setAvatarLoading(false);
    }
  };

  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setAccountLoading(true);

    try {
      // Update profile (name)
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ first_name: firstName, last_name: lastName })
        .eq('id', user.id);

      if (profileError) throw profileError;

      // Check if email needs updating
      const isEmailUpdate = newEmail && newEmail.trim() !== '' && newEmail !== email;
      
      if (isEmailUpdate) {
        // Note: Don't await this - Supabase doesn't resolve the Promise until email is verified
        supabase.auth.updateUser({ email: newEmail })
          .then(({ error }) => {
            if (error) {
              toast.error(error.message || 'Failed to update email');
            }
          });
        
        setNewEmail('');
        toast.success('Verification email sent to your new address. Please check your inbox.');
      } else {
        toast.success('Profile updated successfully');
      }

      // Refresh profile in background
      fetchProfile(user.id).catch(console.error);
    } catch (error: any) {
      console.error('Error updating account:', error);
      // Handle rate limit errors
      if (error?.message?.includes('security purposes') || error?.status === 429) {
        const seconds = error.message.match(/after (\d+) seconds/)?.[1] || '60';
        toast.error(`Please wait ${seconds} seconds before trying again.`);
      } else {
        toast.error(error?.message || 'Failed to update account. Please try again.');
      }
    } finally {
      setAccountLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordLoading(true);

    try {
      if (isOAuthUser) {
        if (newPassword !== confirmPassword) {
          toast.error('Passwords do not match.');
          setPasswordLoading(false);
          return;
        }

        if (newPassword.length < 6) {
          toast.error('Password must be at least 6 characters.');
          setPasswordLoading(false);
          return;
        }

        const { error } = await supabase.auth.updateUser({ password: newPassword });
        if (error) throw error;

        toast.success('Password set successfully! You can now log in with email and password.');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: email,
          password: currentPassword,
        });

        if (signInError) {
          toast.error('Current password is incorrect.');
          setPasswordLoading(false);
          return;
        }

        if (newPassword.length < 6) {
          toast.error('New password must be at least 6 characters.');
          setPasswordLoading(false);
          return;
        }

        const { error } = await supabase.auth.updateUser({ password: newPassword });
        if (error) throw error;

        toast.success('Password updated successfully!');
        setCurrentPassword('');
        setNewPassword('');
      }
    } catch (error: any) {
      console.error('Error updating password:', error);
      // Handle rate limit errors
      if (error?.message?.includes('security purposes') || error?.status === 429) {
        const seconds = error.message.match(/after (\d+) seconds/)?.[1] || '60';
        toast.error(`Please wait ${seconds} seconds before trying again.`);
      } else {
      toast.error('Failed to update password. Please try again.');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    setDeleteLoading(true);

    try {
      // Call the delete-account Edge Function
      // This handles: Stripe cancellation, workspace deletion (if only member), profile deletion
      const { data, error } = await supabase.functions.invoke('delete-account', {
        body: { userId: user.id },
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to delete account');
      }

      // Sign out and redirect
      await signOut();
      onClose();
      navigate('/login');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account. Please try again.');
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  const getInitial = () => {
    if (firstName) {
      return firstName[0].toUpperCase();
    }
    return 'U';
  };

  // Show avatar if it's uploaded or from OAuth provider
  const showAvatar = hasValidAvatar(profile?.avatar_url);

  if (!isVisible) return null;

  return (
    <>
      <div 
        className={`account-modal ${isVisible ? 'is-visible' : ''} ${isAnimating ? 'is-open' : ''} ${!isAnimating && isVisible ? 'is-closing' : ''}`}
      >
        <div className="settings-background" ref={modalRef}>
          <div 
            className="modal-close is-dark-btn"
            onClick={onClose}
          >
            <div className="rotated-text">+</div>
          </div>

          <div className="settings-sidebar">
            <a
              href="#"
              className={`sidebar-link w-inline-block ${activePanel === 'account' ? 'is-active' : ''}`}
              onClick={(e) => { e.preventDefault(); setActivePanel('account'); }}
            >
              <div>Account settings</div>
            </a>
            
            {/* View as: the password and email belong to the signed-in Staff, not the viewed User. */}
            {!viewingAs && (
              <a
                href="#"
                className={`sidebar-link w-inline-block ${activePanel === 'password' ? 'is-active' : ''}`}
                onClick={(e) => { e.preventDefault(); setActivePanel('password'); }}
              >
                <div>{isOAuthUser ? 'Set Account Password' : 'Password'}</div>
              </a>
            )}
            
            <a
              href="https://www.uselima.com/frequently-asked-questions"
              target="_blank"
              rel="noreferrer"
              className="sidebar-link w-inline-block"
            >
              <div>FAQs</div>
            </a>
            
            <a
              href="https://www.uselima.com/contact"
              target="_blank"
              rel="noreferrer"
              className="sidebar-link w-inline-block"
            >
              <div>Support</div>
            </a>

            <div className="bottom-link-wrapper">
              
              <a
                href="#"
                className="sidebar-link is-logout w-inline-block"
                onClick={(e) => { e.preventDefault(); handleSignOut(); }}
              >
                <div className="embed-icon w-embed">
                  <LogOut size={20} />
                </div>
                <div>Logout</div>
              </a>
            </div>
          </div>

          {/* Account Settings Panel */}
          <div 
            className="account-settings-panel"
            style={{ display: activePanel === 'account' ? 'block' : 'none' }}
          >
            <div className="text-size-medium text-weight-semibold">Account settings</div>
            <div className="spacer-small"></div>
            
            <div className="avatar-details">
              <div className="user-avatar is-large">
                {showAvatar && profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt="Avatar" className="avatar-image" />
                ) : (
                  <div className="initial">{getInitial()}</div>
                )}
              </div>
              <div className="avatar-button-flex">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  onChange={handleAvatarChange}
                  style={{ display: 'none' }}
                />
                <a
                  href="#"
                  className="button is-small is-white has-inner-shadow w-button"
                  onClick={(e) => { e.preventDefault(); handleAvatarClick(); }}
                  style={{ opacity: avatarLoading ? 0.6 : 1 }}
                >
                  {avatarLoading ? 'Uploading...' : 'Change avatar'}
                </a>
                {showAvatar && (
                  <div 
                    className="delete-link is-dark"
                    onClick={handleRemoveAvatar}
                    style={{ cursor: 'pointer', opacity: avatarLoading ? 0.6 : 1 }}
                  >
                    Remove avatar
                  </div>
                )}
              </div>
            </div>
            
            <div className="spacer-xsmall"></div>
            
            <div className="account-block w-form">
              <form onSubmit={handleAccountSubmit}>
                <div className="form-flex">
                  <div className="form-flex_left">
                    <label htmlFor="first-name" className="form_label">First name</label>
                    <input
                      className="form_input w-input"
                      maxLength={256}
                      name="first-name"
                      placeholder="Sarah"
                      type="text"
                      id="first-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </div>
                  <div className="form-flex_right">
                    <label htmlFor="last-name" className="form_label">Last name</label>
                    <input
                      className="form_input w-input"
                      maxLength={256}
                      name="last-name"
                      placeholder="Smith"
                      type="text"
                      id="last-name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="spacer-xsmall"></div>
                
                <label htmlFor="email" className="form_label">Email</label>
                <input
                  className="form_input w-input"
                  maxLength={256}
                  name="email"
                  type="email"
                  id="email"
                  value={email}
                  disabled
                />
                
                <div className="spacer-xsmall"></div>
                
                <label htmlFor="new-email" className="form_label">New Email</label>
                <input
                  className="form_input w-input"
                  maxLength={256}
                  name="new-email"
                  placeholder="Enter new email address"
                  type="email"
                  id="new-email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  disabled={!!viewingAs}
                />
                
                <div className="spacer-small"></div>
                
                <input
                  type="submit"
                  className="button is-small w-button"
                  value={accountLoading ? 'Please wait...' : 'Save changes'}
                  disabled={accountLoading}
                />
              </form>
            </div>
            
            {!viewingAs && (
              <div className="delete-wrapper">
                <div 
                  className="delete-link"
                  onClick={() => setShowDeleteModal(true)}
                  style={{ cursor: 'pointer' }}
                >
                  Delete account
                </div>
              </div>
            )}
          </div>

          {/* Password Panel */}
          <div 
            className="password-panel"
            style={{ display: activePanel === 'password' ? 'block' : 'none' }}
          >
            <div className="text-size-medium text-weight-semibold">
              {isOAuthUser ? 'Set Account Password' : 'Password'}
            </div>
            <div className="spacer-small"></div>
            
            {isOAuthUser && (
              <>
                <div className="text-size-small text-colour-alt-grey">
                  You signed up with Google. Set a password to also log in with email and password.
                </div>
                <div className="spacer-small"></div>
              </>
            )}
            
            <div className="account-block w-form">
              <form onSubmit={handlePasswordSubmit}>
                {!isOAuthUser && (
                  <>
                    <label htmlFor="current-password" className="form_label">Current Password</label>
                    <input
                      className="form_input w-input"
                      maxLength={256}
                      name="current-password"
                      type="password"
                      id="current-password"
                      required
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                    <div className="spacer-xsmall"></div>
                  </>
                )}
                
                <label htmlFor="new-password" className="form_label">New Password</label>
                <input
                  className="form_input w-input"
                  maxLength={256}
                  name="new-password"
                  type="password"
                  id="new-password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                
                {isOAuthUser && (
                  <>
                    <div className="spacer-xsmall"></div>
                    <label htmlFor="confirm-password" className="form_label">Confirm Password</label>
                    <input
                      className="form_input w-input"
                      maxLength={256}
                      name="confirm-password"
                      type="password"
                      id="confirm-password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </>
                )}
                
                <div className="spacer-small"></div>
                
                <input
                  type="submit"
                  className="button is-small w-button"
                  value={passwordLoading ? 'Please wait...' : isOAuthUser ? 'Set password' : 'Update password'}
                  disabled={passwordLoading}
                />
              </form>
            </div>
          </div>
        </div>

        <div className="close-layer" onClick={onClose}></div>
      </div>

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
        title="Delete account?"
        message="This action is permanent and your account cannot be restored. All your designs and data will be deleted."
        confirmText="Yes, delete account"
        loading={deleteLoading}
      />
    </>
  );
}
